/// <amd-module name="Build/Designer/YamlSidePanel" />
import { AddedSnippet } from "Build/Designer/Resources";
import { YamlAssistantProvider, YamlGeneratedEvent, YamlGeneratedType } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import * as React from "react";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { css } from "VSSUI/Util";

export interface IYamlSidePanelProps {
    editor: monaco.editor.ICodeEditor;
    isOpen: boolean;
    onDismiss(): void;
}

interface IYamlSidePanelState {
}

const yamlAssistantDependencies = [
    "ms.vss-build-web.designer-extensions.yaml-assistant",
    "ms.vss-build-web.yaml-assistant-data-provider"
];

export class YamlSidePanel extends VssComponent<IYamlSidePanelProps, IYamlSidePanelState> {
    private _yamlAssistantDependenciesPromise: Promise<any>;

    public componentDidUpdate(prevProps: Readonly<IYamlSidePanelProps>, prevState: Readonly<IYamlSidePanelState>) {
        super.componentDidUpdate(prevProps, prevState);

        // After the editor is ready for user interaction, asynchronously fetch the Yaml Assistant dependences and register
        // the YamlAssistantViewService in case it's requested by YAML "Settings" code lens to view the corresponding Task.
        if (this.props.editor && !this._yamlAssistantDependenciesPromise) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._yamlAssistantDependenciesPromise = contributionService.getContributionsAsync(yamlAssistantDependencies);
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.isOpen || !this.props.editor) {
            return null;
        }

        return <div className={css("flex-noshrink flex-column yaml-side-panel", !this.props.isOpen && "hidden")}>
            <WrappedComponent<YamlAssistantProvider>
                dependencies={yamlAssistantDependencies}
                wrappedType={"yaml-assistant-view"}
                wrappedProps={{
                    onYamlGenerated: this._onYamlGenerated,
                    onDismiss: this.props.onDismiss
                }} />
        </div>
    }

    private _onYamlGenerated = (yamlGeneratedEvent: YamlGeneratedEvent) => {
        let contextString = "";

        switch (yamlGeneratedEvent.eventType) {
            case YamlGeneratedType.Snippet:
            default:
                contextString = AddedSnippet;
        }

        this._insertText(yamlGeneratedEvent.yamlString, contextString);
    }

    private _insertText = (text: string, context: string) => {
        if (text.lastIndexOf("\r\n") === text.length - 2) {
            text = text.substr(0, text.length - 2);
        }

        const splitText = text.split("\r\n");
        const currentSelection = this.props.editor.getSelection()!;
        const numLines = splitText.length;
        const lengthOfLastLine = splitText[numLines - 1].length + 1;
        const currentLineContent = this.props.editor.getModel()!.getLineContent(currentSelection.startLineNumber)!;

        // Four cases
        // 1. The cursor is in the middle of a random line. Insert text on next line instead of breaking current line
        // 2. The cursor is on a whitespace only line. Insert text on current line, trying to preserve the current tab level based on where the cursor is at
        // 3. A task block is selected. Replace the selection while preserving tab depth
        // 4. Some arbitrary text is selected. Replace the existing selected text
        if (currentSelection.startColumn === currentSelection.endColumn && currentSelection.startLineNumber === currentSelection.endLineNumber) {
            if (/\S/.test(currentLineContent)) {
                this._moveCursorToEndOfLineAndInsert(text, context, currentSelection, numLines, lengthOfLastLine);
            }
            else {
                this._insertWhilePreservingTabDepth(text, splitText, context, currentLineContent, currentSelection, numLines, lengthOfLastLine);
            }
        }
        else {
            if (currentSelection.startLineNumber !== currentSelection.endLineNumber && currentSelection.startColumn === 1 && currentLineContent.indexOf(splitText[0]) >= 0) {
                this._replaceTask(text, splitText, context, currentLineContent, currentSelection, lengthOfLastLine);
            }
            else {
                this._replaceCurrentlySelectedText(text, context, currentSelection, numLines, lengthOfLastLine);
            }
        }
    }

    private _moveCursorToEndOfLineAndInsert(text: string, context: string, currentSelection: monaco.Selection, numLines: number, lengthOfLastLine: number) {
        const maxColumn = this.props.editor.getModel()!.getLineMaxColumn(currentSelection.startLineNumber);
        text = "\r\n" + text;
        const insertionRange = this._createRange(
            currentSelection.startLineNumber,
            maxColumn,
            currentSelection.startLineNumber,
            maxColumn);
        const finalSelection = this._createSelection(
            currentSelection.startLineNumber + 1,
            1,
            currentSelection.startLineNumber + numLines,
            lengthOfLastLine + 1);
        this._insertTextToMonaco(text, context, insertionRange, finalSelection);
    }

    private _insertWhilePreservingTabDepth(text: string, splitText: string[], context: string, currentLineContent: string, currentSelection: monaco.Selection, numLines: number, lengthOfLastLine: number) {
        const maxColumn = this.props.editor.getModel()!.getLineMaxColumn(currentSelection.startLineNumber);
        const preWhiteSpace = currentLineContent.substr(0, currentSelection.startColumn - 1);
        text = splitText.join("\r\n" + preWhiteSpace);
        const insertionRange = this._createRange(
            currentSelection.startLineNumber,
            currentSelection.startColumn,
            currentSelection.endLineNumber,
            maxColumn);
        const finalSelection = this._createSelection(
            currentSelection.startLineNumber,
            1,
            currentSelection.startLineNumber + numLines - 1,
            lengthOfLastLine + preWhiteSpace.length + 1);
        this._insertTextToMonaco(text, context, insertionRange, finalSelection);
    }

    private _replaceTask(text: string, splitText: string[], context: string, currentLineContent: string, currentSelection: monaco.Selection, lengthOfLastLine: number) {
        const currentLineContentWithoutSpace = currentLineContent.trim();
        const preWhiteSpace = currentLineContent.substr(0, currentLineContent.indexOf(currentLineContentWithoutSpace[0]));
        text = preWhiteSpace + splitText.slice(1).join("\r\n" + preWhiteSpace);

        // Rather than replace the current selection, only replace the 'inputs' block so as to not stomp on non-input yaml content
        // however, use the full task block as starting and final selection so that conceptually, the entire task is being 'worked' on
        let inputsLineStart = -1;
        let inputsLineEnd = -1;
        const model = this.props.editor.getModel()!;
        const inputsWhiteSPace = preWhiteSpace + "  ";
        for (let i = currentSelection.startLineNumber; i <= currentSelection.endLineNumber; ++i) {
            if (model.getLineContent(i) === inputsWhiteSPace + "inputs:") {
                inputsLineStart = i;
            }
            else if (inputsLineStart > 0) {
                const iLine = model.getLineContent(i);
                const iLineTrimmed = iLine.trim();
                const iLinepreWhiteSpace = iLine.substr(0, iLine.indexOf(iLineTrimmed[0]));
                if (iLinepreWhiteSpace === inputsWhiteSPace) {
                    inputsLineEnd = i - 1;
                    break;
                }
            }
        }

        if (inputsLineEnd === -1 && inputsLineStart === -1){
            // no input list is present but there may be other parameters for the task (eg displayname)
            if (text != ""){
                // add a new line at the end to avoid overwriting next line content
                text += "\r\n";
            }

            const insertionRange = this._createRange(
                currentSelection.startLineNumber + 1,
                1,
                currentSelection.startLineNumber + 1,
                1);

            const lastColumnPosition = 9999; // arbitrarily large to include the actual last column that Monaco will select

            const finalSelection = this._createSelection(
                currentSelection.startLineNumber,
                currentSelection.startColumn,
                currentSelection.startLineNumber + splitText.length - 1,
                lastColumnPosition);

            // need new line in case of tasks at the end of the file  (no new line after)
            this._insertTextToMonaco(text, context, insertionRange, finalSelection);
        }
        else {
            if (inputsLineStart === -1) {
                inputsLineStart = currentSelection.startLineNumber;
            }
    
            if (inputsLineEnd === -1) {
                inputsLineEnd = currentSelection.endLineNumber;
            }
    
            const insertionRange = this._createRange(
                inputsLineStart,
                1,
                inputsLineEnd,
                model.getLineMaxColumn(inputsLineEnd));
    
            const linesAdded = (splitText.length - 1) - (inputsLineEnd - inputsLineStart + 1);
            const lengthOfOriginalSelection = currentSelection.endLineNumber - currentSelection.startLineNumber + 1;
            const lastColumnPosition = 9999; // arbitrarily large to include the actual last column that Monaco will select
    
            const finalSelection = this._createSelection(
                currentSelection.startLineNumber,
                currentSelection.startColumn,
                currentSelection.startLineNumber + lengthOfOriginalSelection + linesAdded - 1,
                lastColumnPosition);
            this._insertTextToMonaco(text, context, insertionRange, finalSelection);    
        }

    }

    private _replaceCurrentlySelectedText(text: string, context: string, currentSelection: monaco.Selection, numLines: number, lengthOfLastLine: number) {
        const insertionRange = this._createRange(
            currentSelection.startLineNumber,
            currentSelection.startColumn,
            currentSelection.endLineNumber,
            currentSelection.endColumn);
        const finalSelection = this._createSelection(
            currentSelection.startLineNumber,
            currentSelection.startColumn,
            currentSelection.startLineNumber + numLines - 1,
            lengthOfLastLine + 1);
        this._insertTextToMonaco(text, context, insertionRange, finalSelection);
    }

    private _insertTextToMonaco(text: string, context: string, insertionRange: monaco.Range, finalSelection: monaco.Selection) {
        const id = { major: 1, minor: 1 };
        const op = { identifier: id, range: insertionRange, text: text, forceMoveMarkers: true };

        this.props.editor.executeEdits(context, [op], [finalSelection]);
        this.props.editor.focus();

        this._sendToast(context);
    }

    private _sendToast(message: string) {
        const toastService = this.context && this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        toastService && toastService.addToast({ message, duration: 2000 });
    }

    // move these to functions so that tests can mock them and not depend on monaco
    private _createRange(startLineNumber: number, startColumn: number, endLineNumber: number, endColumn: number) {
        return new monaco.Range(startLineNumber, startColumn, endLineNumber, endColumn);
    }

    private _createSelection(selectionStartLineNumber: number, selectionStartColumn: number, positionLineNumber: number, positionColumn: number) {
        return new monaco.Selection(selectionStartLineNumber, selectionStartColumn, positionLineNumber, positionColumn);
    }
}
